import { PencilIcon } from "@heroicons/react/solid";
import React, { useEffect } from "react";
import {
  Link,
  matchPath,
  RouteComponentProps,
  useHistory
} from "react-router-dom";
import {
  Area,
  ComposedChart,
  LabelList,
  Line,
  ResponsiveContainer,
  Tooltip
} from "recharts";
import { TooltipProps } from "recharts/types/component/Tooltip";
import CenteredLoading from "../../components/CenteredLoading";
import ChevronDownIcon from "../../components/icons/ChevronDownIcon";
import CloseIcon from "../../components/icons/CloseIcon";
import { useAppearance } from "../../context/appearance";
import { useLineOfSight } from "../../context/lineOfSight";
import { useLocations } from "../../context/locations";
import classNames from "../../utils/classNames";
import useMapConfig from "../../utils/useMapConfig";

interface Props extends RouteComponentProps<{}> {}

const LineOfSight: React.FC<Props> = ({ location: { pathname } }) => {
  const { setSelectPointsOnMapConfig } = useLocations();
  const { isDark } = useAppearance();
  const [distanceUnit, renderDistance] = useMapConfig((state) => [
    state.distanceUnit,
    state.renderDistance
  ]);
  const {
    startPoint,
    endPoint,
    startHeight,
    endHeight,
    pointsData,
    setStartHeight,
    setEndHeight,
    chartData,
    clearPoints,
    setIsMinimized,
    distance,
    setIsEditingStart,
    setIsEditingEnd,
    lineOfSightLineColor,
    clearLineOfSight
  } = useLineOfSight();
  const history = useHistory();
  const pathMatch = matchPath<{
    tab: "hotspots" | "locations" | "hexes" | "contacts" | "installs";
  }>(pathname, {
    path: "/:tab/line-of-sight"
  });

  useEffect(() => {
    const defaultPageContainer = document.getElementById(
      "default-page-container"
    );

    defaultPageContainer?.scrollTo({
      top:
        defaultPageContainer.scrollHeight - defaultPageContainer.clientHeight,
      behavior: "smooth"
    });
  }, [chartData]);

  useEffect(() => {
    if (!startPoint || !endPoint) {
      history.push(`/${pathMatch?.params.tab}`);
    }
  }, [endPoint, history, pathMatch?.params.tab, startPoint]);

  const renderCustomizedLabel = (props: any) => {
    const {
      x,
      y,
      index
    }: {
      x: number;
      y: number;
      index: number;
    } = props;

    let elevation = 0;
    const elementsColor = isDark ? "#fff" : "#555555";

    if (
      index !== pointsData.firstStartIndex &&
      index !== pointsData.firstEndIndex
    )
      return null;

    if (index === pointsData.firstStartIndex) {
      elevation = startHeight;
    }

    if (index === pointsData.firstEndIndex) {
      elevation = endHeight;
    }

    return (
      <svg>
        <line
          z={10}
          x1={x}
          y1={y}
          x2={x}
          y2={1000}
          style={{ stroke: elementsColor, strokeWidth: 1 }}
        />

        <svg width="22" height="26" x={x - 11} y={y - 24}>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M3.57538 3.07538C7.67588 -1.02513 14.3241 -1.02513 18.4246 3.07538C22.5251 7.17588 22.5251 13.8241 18.4246 17.9246L11 25.3492L3.57538 17.9246C-0.525126 13.8241 -0.525126 7.17588 3.57538 3.07538ZM11 13.5C12.6569 13.5 14 12.1569 14 10.5C14 8.84315 12.6569 7.5 11 7.5C9.34315 7.5 8 8.84315 8 10.5C8 12.1569 9.34315 13.5 11 13.5Z"
            fill={
              index === pointsData.firstStartIndex ? elementsColor : "#a96ceb"
            }
          />
        </svg>

        <g>
          <text
            style={{ userSelect: "none" }}
            fill={elementsColor}
            fontWeight="bold"
            fontSize={12}
            y={y - 28}
            x={x - 15}
            y2={y}
          >
            {elevation.toFixed(1)} {distanceUnit}
          </text>
        </g>
      </svg>
    );
  };

  const RenderTooltip = (props: TooltipProps<"", "">) => {
    if (!props.active || !props.payload) return null;

    const { payload } = props.payload[0];

    if (!payload) return null;

    return (
      <div className="px-4 py-2 border rounded bg-gray-50 dark:bg-gray-800">
        {payload.line && (
          <p className="text-sm">
            {`Height: ${(payload?.line - payload?.elevation).toFixed(
              1
            )}${distanceUnit}`}
          </p>
        )}

        <p className="text-sm">{`Elevation: ${payload?.elevation.toFixed(
          1
        )}${distanceUnit}`}</p>
      </div>
    );
  };

  return (
    <>
      <article className="max-w-full mx-auto py-4 px-4 sm:px-6 lg:px-8">
        <nav className="py-3 px-2 flex items-center justify-between sm:px-0">
          <div className="flex-1 flex justify-between align-center">
            <p className="text-xl sm:text-4xl font-bold text-gray-900 dark:text-white truncate inline-flex items-center">
              Line of sight
            </p>
            <div className="flex">
              <Link
                replace
                to={`/${pathMatch?.params.tab}`}
                onClick={() => setIsMinimized(true)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-default dark:bg-gray-900 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-800 dark:border-gray-700 dark:hover:border-gray-400 dark:disabled:hover:border-gray-700 dark:disabled:hover:bg-gray-900 dark:disabled:hover:text-gray-400"
              >
                <ChevronDownIcon className="h-5 w-5" />
              </Link>

              <span
                onClick={() => {
                  clearPoints();
                  history.push(`/${pathMatch?.params.tab}`);
                }}
                className="ml-3 cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-default dark:bg-gray-900 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-800 dark:border-gray-700 dark:hover:border-gray-400 dark:disabled:hover:border-gray-700 dark:disabled:hover:bg-gray-900 dark:disabled:hover:text-gray-400"
              >
                <CloseIcon className="h-5 w-5" />
              </span>
            </div>
          </div>
        </nav>
        <p className="text-base px-2 inline-flex items-center sm:px-0">
          Enter the desired height to update the hotspot installation heights.
        </p>
        <div className="flex max-w-full justify-start mt-4">
          <div className="flex items-baseline mt-1 mr-2 text-2xl font-semibold text-gray-900 dark:text-white">
            <button
              onClick={() => {
                setIsEditingStart(true);
                history.push(`/${pathMatch?.params.tab}`);
                setSelectPointsOnMapConfig({
                  coordinates: startPoint,
                  lineOfSightContext: {
                    pointType: "start"
                  }
                });
              }}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-default dark:bg-gray-900 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-800 dark:border-gray-700 dark:hover:border-gray-400 dark:disabled:hover:border-gray-700 dark:disabled:hover:bg-gray-900 dark:disabled:hover:text-gray-400"
            >
              <PencilIcon className="h-4 w-4 -ml-1 mr-2" />
              Point A
            </button>
          </div>
          <div className="flex items-baseline mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
            <button
              onClick={() => {
                setIsEditingEnd(true);
                history.push(`/${pathMatch?.params.tab}`);
                setSelectPointsOnMapConfig({
                  coordinates: endPoint,
                  lineOfSightContext: {
                    pointType: "end"
                  }
                });
              }}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-default dark:bg-gray-900 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-800 dark:border-gray-700 dark:hover:border-gray-400 dark:disabled:hover:border-gray-700 dark:disabled:hover:bg-gray-900 dark:disabled:hover:text-gray-400"
            >
              <PencilIcon className="h-4 w-4 -ml-1 mr-2" />
              Point B
            </button>
          </div>
        </div>

        <div className="mt-8 border rounded-xl dark:border-gray-600 overflow-hidden">
          <div className="flex w-full justify-between items-center bg-gray-50 dark:bg-gray-800 p-2 rounded-t-xl">
            <div className="flex flex-col items-center p-4 md:flex-row md:space-x-4">
              <p>Point A</p>
              <div className="flex w-28 items-center mx-auto bg-gray-100 dark:bg-gray-900 rounded-lg pr-8">
                <input
                  name="start"
                  type="number"
                  value={startHeight}
                  min={0}
                  onChange={(e) => setStartHeight(Number(e.target.value))}
                  className="min-w-full w-28 bg-transparent border-none text-right"
                />
                <p>{distanceUnit}</p>
              </div>
            </div>

            <div className="hidden sm:flex w-3/6	border-b border-b-opacity-5">
              {distance && (
                <p className="w-full ml-2 mr-2 text-center">
                  {renderDistance(distance, distance > 1000)}
                </p>
              )}
            </div>

            <div className="flex flex-col items-center p-4 space-x-4 md:flex-row">
              <p>Point B</p>
              <div className="flex w-28 items-center mx-auto bg-gray-100 dark:bg-gray-900 rounded-lg pr-8">
                <input
                  type="number"
                  name="end"
                  value={endHeight}
                  min={0}
                  onChange={(e) => setEndHeight(Number(e.target.value))}
                  className="min-w-full w-28 bg-transparent border-none text-right"
                />
                <p>{distanceUnit}</p>
              </div>
            </div>
          </div>

          <div className="flex sm:hidden p-4 items-center justify-between border-t border-b border-gray-600">
            <p
              className={classNames(
                "rounded-xl pl-2 pr-2 bg-opacity-20 text-sm",
                clearLineOfSight
                  ? "bg-green-500 text-green-400"
                  : "bg-red-500 text-red-400"
              )}
            >
              {clearLineOfSight ? "visible" : "blocked"}
            </p>
            {distance && (
              <p className="text-gray-400 text-sm">
                Distance:{" "}
                <span className="text-gray-300">
                  {renderDistance(distance, distance > 1000)}
                </span>
              </p>
            )}
          </div>

          <dl className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {chartData ? (
              <div className="flex items-start justify-center w-full h-56 sm:col-span-2">
                <ResponsiveContainer>
                  <ComposedChart className="mt-2" data={chartData}>
                    <Tooltip
                      cursor={{ stroke: "none" }}
                      content={(props: TooltipProps<"", "">) => (
                        <RenderTooltip {...props} />
                      )}
                    />

                    <Line
                      dataKey="heightLimitLine"
                      dot={false}
                      activeDot={{ r: 0 }}
                      stroke={"#111827"}
                      isAnimationActive={false}
                    />

                    <Line
                      dataKey="line"
                      dot={false}
                      stroke={lineOfSightLineColor}
                      isAnimationActive={false}
                    >
                      <LabelList content={renderCustomizedLabel} />
                    </Line>

                    <Area
                      className="pb-2"
                      dataKey="elevation"
                      stroke="#a96ceb"
                      fill={`${isDark ? "#352952" : "#a96ceb"}`}
                      fillOpacity={1}
                      isAnimationActive={false}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="bg-white shadow rounded-lg sm:col-span-2 dark:bg-gray-800">
                <CenteredLoading className="h-52" />
              </div>
            )}
          </dl>
        </div>
      </article>
    </>
  );
};

export default LineOfSight;
